import { toast } from "@/components/common/Toast/Toast"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useState } from "react"
import { useVerifyStatement } from "../../../../../api/declarationsApi"
import type { WorklistIssue } from "../../studio/cellState"
import { signStep3 } from "../footer/useConformFramework"
import { useConditionRows, useReviewMode } from "../reviewMode"
import type { StatementCard } from "../useStudioV2State"
import { SubstanceEditor } from "./SubstanceEditor"

/** The focused step that takes over the review-mask pane for the STOFFE row
 *  (review-mask prototype, round 6): the substance table, beside the
 *  still-live PDF. */
export type SubstanceSheet = {
  kind: "substances"
  cardId: string
  /** The chip the reviewer clicked: that substance's per-article rows start
   *  open. Null: the row's plain edit button. */
  focusKey: string | null
}

export interface SubstanceEditorSheetProps {
  declId: string
  card: StatementCard
  /** The card's position among the Verordnung's cards: the header names the
   *  statement ("Aussage N") because the card itself is no longer on screen. */
  index: number
  poolEmpty: boolean
  articleNames: Map<string, string>
  focusKey: string | null
  onClose: () => void
  /** This card's own step-3 worklist issue (W5 step3-chip): gates the header's
   *  "Bestätigen" button, enabled only while `state === "needs-verification"`.
   *  Undefined (every caller/test predating this prop, and a card with no open
   *  step-3 issue at all): the button renders disabled with the "nothing to
   *  confirm" hint, same as a `ready` issue (see `ConfirmStep3Button`'s own
   *  doc, below in this file). */
  step3Issue?: WorklistIssue
}

/**
 * Hosts `SubstanceEditor` in place of the statement cards (the same
 * `sv2-sheet` takeover as `ArticleBrowserSheet`) rather than in
 * `SubstanceRow`'s old blocking dialog: the document pane stays live beside
 * it, so the reviewer reads a value off the PDF and types it into the table
 * without closing anything (the whole point of the side-by-side workspace).
 * Every write inside is an immediate per-field commit
 * (`useConditionFieldWrites`), so "Fertig" only closes the sheet.
 *
 * "Bestätigen" (W5 step3-chip, product ask 2026-09-17) is the per-card twin
 * of "Verordnung als geprüft markieren": it signs THIS card's own step-3
 * articles the same way, through the extracted `signStep3` (no second copy
 * of the marks/merge/PUT, see that function's own doc).
 */
export function SubstanceEditorSheet({
  declId,
  card,
  index,
  poolEmpty,
  articleNames,
  focusKey,
  onClose,
  step3Issue,
}: SubstanceEditorSheetProps) {
  const { translate } = useTranslation()
  return (
    <div
      className="sv2-sheet"
      data-testid="substance-editor"
      onKeyDown={(e) => {
        // Escape closes THIS sheet, never the whole workspace (same contract
        // as `ArticleBrowser`'s own Esc).
        if (e.key !== "Escape") return
        e.stopPropagation()
        onClose()
      }}
    >
      <div className="sv2-sshead">
        <span className="sv2-ssnum">
          {translate("MC_DECL_V2_UNIT_LABEL", { i: index + 1 })}
        </span>
        <span className="sv2-sstitle">
          {translate("MC_DECL_V2_UNIT_SUBSTANCES_DIALOG_TITLE")}
        </span>
        <ConfirmStep3Button
          declId={declId}
          framework={card.framework}
          step3Issue={step3Issue}
        />
        <button type="button" className="sv2-hact" onClick={onClose}>
          {translate("MC_DECL_V2_SUBST_SHEET_DONE")}
        </button>
      </div>
      <div className="sv2-ssbody">
        <SubstanceEditor
          declId={declId}
          card={card}
          poolEmpty={poolEmpty}
          articleNames={articleNames}
          focusKey={focusKey}
        />
      </div>
    </div>
  )
}

/**
 * THE PER-CARD STEP-3 CONFIRM. Enabled only while this card's step-3 issue
 * is `needs-verification` (fields complete, unsigned): that is the ONE state
 * this button can actually clear. Disabled otherwise, with a tooltip that
 * never invents a number this component would have to recompute: a
 * `needs-input` issue's own count is already on screen two rows below, in
 * `SubstanceEditor`'s own "N Pflichtfelder offen" banner (`openCounts`'s own
 * doc: deliberately NOT the same count as a worklist issue's `articleIds`,
 * so this hint points at that banner rather than restating a different
 * number next to it). No issue at all, or a `ready` issue (signed, a guided
 * resolution still awaits Continue elsewhere: `issueReasonKey`'s own doc):
 * the same "nothing to confirm here" hint, since neither is something this
 * button, or anything else in the V3 mask, can act on.
 *
 * On success the card's step-3 issue disappears on its own: `signStep3`'s
 * verify command lands in the SAME condition-fields cache
 * (`useVerifyStatement`'s own `onSuccess`, a synchronous `setQueryData`,
 * not a refetch) that `useDeclarationFlowState` derives the worklist from,
 * so `ReviewMaskPane`'s `step3Issue` recomputes to undefined on the next
 * render with no local override needed here.
 *
 * DOES NOT AWAIT THE SIBLING EDITOR'S PENDING SAVE CHAIN (TUR-0000). The
 * server derives the open cells from the STORED rows, so a Verify fired the
 * instant after a keystroke could sign against a value one keystroke stale
 * if that edit's own save has not landed yet. This button and
 * `SubstanceEditor` are SIBLINGS here (the header and the body of one
 * sheet), each mounting its OWN `useConditionFieldWrites` instance with its
 * own `saveChainRef` — nothing here can reach the editor's actual chain to
 * await it. The guarantee instead is `useVerifyStatement`'s `onSuccess`
 * invalidation (`declarationsApi.ts`): any save that lands after this
 * verify's response is not read as this verify's own truth, and the
 * worklist recomputes off the refetch. Lifting `useConditionFieldWrites` to
 * a shared parent so this button could reach the editor's actual chain is a
 * real fix, left for a follow-up — it changes `SubstanceEditor`'s own prop
 * contract, out of scope here.
 */
function ConfirmStep3Button({
  declId,
  framework,
  step3Issue,
}: {
  declId: string
  framework: string
  step3Issue?: WorklistIssue
}) {
  const { translate } = useTranslation()
  const verifyStatement = useVerifyStatement()
  const conditionFieldsQuery = useConditionRows(declId)
  const { paged } = useReviewMode()
  const [confirming, setConfirming] = useState(false)

  const enabled = step3Issue?.state === "needs-verification" && !confirming

  const handleConfirm = async () => {
    if (!step3Issue || step3Issue.state !== "needs-verification") return
    setConfirming(true)
    const result = await signStep3(verifyStatement, {
      declId,
      statementId: step3Issue.statementId ?? step3Issue.unitId,
      framework,
      state: step3Issue.state,
      articleIds: step3Issue.articleIds,
      rows: conditionFieldsQuery.data?.rows ?? [],
      saveOrder: paged,
    })
    setConfirming(false)
    if (result.ok) toast.success(translate("MC_DECL_V2_SUBST_CONFIRM_SUCCESS"))
    else if (result.reason === "stale-view")
      toast.error(translate("MC_DECL_V2_SUBST_CONFIRM_STALE"))
    else toast.error(translate("MC_DECL_V2_SUBST_CONFIRM_FAILED"))
  }

  const button = (
    <button
      type="button"
      className="sv2-hact"
      disabled={!enabled}
      onClick={() => void handleConfirm()}
    >
      {translate("MC_DECL_V2_SUBST_CONFIRM_BUTTON")}
    </button>
  )
  if (enabled) return button

  // The wrapping span carries the tooltip, not the button itself: a
  // `disabled` native button never fires hover/focus listeners a Radix
  // trigger relies on (same reasoning `ReviewMaskPane`'s own disabled-button
  // tooltip documents for `.sv2-act[disabled]`).
  const hint =
    step3Issue?.state === "needs-input"
      ? "MC_DECL_V2_SUBST_CONFIRM_MISSING_HINT"
      : "MC_DECL_V2_SUBST_CONFIRM_NOTHING_HINT"
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">{button}</span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[280px]">
          {translate(hint)}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
